import React, { useCallback, useMemo } from 'react';
import { FieldErrors, SetValueConfig, TriggerConfig, UseFormSetValue, UseFormTrigger } from 'react-hook-form';

import ValueExpression from '../../../valueexpressions/models/valueexpressions/ValueExpression';
import { ToggleButtonGroup } from '../../../common/components/ButtonGroup';
import { ToggleButton } from '../../../common/components/ToggleButton';
import ThresholdValueSelector, { ThresholdValueColorVariant } from '../ThresholdValueSelector';
import { PositiveDirection } from '../../../metrics2/models/entities/PositiveDirection';

import Styles from './KpiCustomThresholdSelection.module.scss';
import { FieldValues } from 'react-hook-form/dist/types';

type Props<T extends FieldValues> = {
  valueExpression: ValueExpression;
  getValues: (id: string) => string;
  watch: <T>(id: string) => T;
  errors: FieldErrors;
  setValue: UseFormSetValue<T>;
  trigger: UseFormTrigger<T>;
};

type LimitModeType = 'up' | 'down';

export const useGoalInput = <T extends FieldValues>(
  getValues: (id: string) => string,
  setValue: UseFormSetValue<T>,
  trigger: UseFormTrigger<T>,
  defaultDirection: PositiveDirection
) => {
  const setGoal = useCallback(
    (formValueField: 'positiveVal' | 'negativeVal') => (v) => {
      if (defaultDirection) {
        // @ts-ignore
        setValue('direction', defaultDirection);
      }

      if (typeof v !== 'number' || isNaN(v)) {
        // @ts-ignore
        setValue(formValueField, null, {
          shouldValidate: true,
        });
        // @ts-ignore
        trigger(['positiveVal', 'negativeVal']);
        return;
      }
      // @ts-ignore
      setValue(formValueField, v, {
        shouldValidate: true,
      });
      // @ts-ignore
      trigger(['positiveVal', 'negativeVal']);
    },
    [setValue, trigger, defaultDirection]
  );
  const setPositiveGoal = useMemo(() => setGoal('positiveVal'), [setGoal]);
  const setNegativeGoal = useMemo(() => setGoal('negativeVal'), [setGoal]);

  return {
    setPositiveGoal,
    setNegativeGoal,
  };
};

export const CustomGoal = <T extends FieldValues>({
  getValues,
  watch,
  setValue,
  valueExpression,
  errors,
  trigger,
}: Props<T>) => {
  const onPositiveValueFocusOut = useCallback(() => {
    const positiveVal = getValues('positiveVal');
    const negativeVal = getValues('negativeVal');

    if (negativeVal == null) {
      // @ts-ignore
      setValue('negativeVal', positiveVal, {
        shouldValidate: true,
      });
      // @ts-ignore
      trigger(['negativeVal', 'positiveVal']);
    }
  }, [getValues, setValue, trigger]);

  const onToggleChange = useCallback(
    (mode: LimitModeType | null) => {
      if (!mode) {
        return;
      }
      // @ts-ignore
      setValue('direction', mode, {
        shouldValidate: true,
        shouldDirty: true,
      });
      // @ts-ignore
      trigger(['positiveVal', 'negativeVal']);
    },
    [setValue, trigger]
  );

  const { setPositiveGoal, setNegativeGoal } = useGoalInput(getValues, setValue, trigger, null);

  const positiveSelector = (
    <>
      <span className={Styles.Name}>
        <strong>grün</strong> wenn
      </span>
      <div className={Styles.ToggleWrapper}>
        <ToggleButtonGroup onChange={(e, value) => onToggleChange(value as LimitModeType)} value={watch('direction')}>
          <ToggleButton value={'up'}>größer</ToggleButton>
          <ToggleButton value={'down'}>kleiner</ToggleButton>
        </ToggleButtonGroup>
      </div>
      <div className={Styles.ThresholdWrapper}>
        <ThresholdValueSelector
          onValueChange={setPositiveGoal}
          tabIndex={1}
          value={watch('positiveVal')}
          valueExpression={valueExpression}
          colorVariant={ThresholdValueColorVariant.Positive}
          onBlur={onPositiveValueFocusOut}
        />
      </div>

      {!!errors?.positiveVal && (
        <div className={Styles.Error}>
          <>{(errors?.positiveVal.message as string)?.replace('positiveVal', 'Wert')}</>
        </div>
      )}
    </>
  );

  const negativeSelector = (
    <>
      <span className={Styles.Name}>
        <strong>rot</strong> wenn
      </span>
      <div className={Styles.ToggleWrapper}>
        <ToggleButtonGroup
          onChange={(e, value) => onToggleChange(value as LimitModeType)}
          value={watch('direction')}
          exclusive>
          {/* notice: This is reversed here, to always be the opposite of the green value */}
          <ToggleButton value={'down'}>größer</ToggleButton>
          <ToggleButton value={'up'}>kleiner</ToggleButton>
        </ToggleButtonGroup>
      </div>
      <div className={Styles.ThresholdWrapper}>
        <ThresholdValueSelector
          onValueChange={setNegativeGoal}
          tabIndex={2}
          valueExpression={valueExpression}
          colorVariant={ThresholdValueColorVariant.Negative}
          value={watch('negativeVal')}
        />
      </div>
      {!!errors?.negativeVal && (
        <div className={Styles.Error}>
          <>{(errors?.negativeVal.message as string)?.replace('negativeVal', 'Wert')}</>
        </div>
      )}
    </>
  );

  return (
    <>
      <div className={Styles.Selector}>
        <div className={Styles.LimitWrapper}>{positiveSelector}</div>
      </div>
      <div className={Styles.Selector}>
        <div className={Styles.LimitWrapper}>{negativeSelector}</div>
      </div>
    </>
  );
};
